// Internal helpers used by `index.tsx`. Kept separate from `utils.ts` because
// `utils.ts` is imported by every generated icon component, so anything we add
// there gets pulled into every icon's module graph; this file is only loaded
// alongside the main `Icon` entry point.

import * as allIcons from './generated/index';

export { allIcons };

/**
 * The canonical name of a Zinnia icon---i.e. the name of an actual export from
 * `./generated/index`. Every `IconCanonicalName` is also an `IconName`, but
 * the reverse is not true: `IconName` also includes alternate-casing aliases.
 */
export type IconCanonicalName = keyof typeof allIcons;

/**
 * Casing-only aliases for icon names. Each key is an alternate casing a caller
 * may type (typically the upper- or lower-cased acronym form), and the value is
 * the real canonical export in `allIcons` that it resolves to.
 *
 * We would prefer to express these as filesystem symlinks (see `README.md` > "I
 * need to add an icon alias"), but macOS's case-insensitive filesystem makes
 * that impossible: e.g. `IconNavApiByZapier.tsx` and `IconNavAPIByZapier.tsx`
 * would collide on disk, so we can't represent both casings as files. This
 * runtime map fills that gap.
 *
 * Every value must exist as an entry in `packages/zinnia-icons/svg`; if you
 * rename an export, update the value here. List every canonical name that
 * contains an acronym, with both its upper- and lower-cased acronym
 * counterpart, so callers can pick whichever casing they prefer.
 *
 * Declared with `as const satisfies` so the keys can be derived as a literal
 * union type (`IconNameAlias`) and the values are validated to be real
 * canonical icon names at compile time.
 */
export const iconNameAliases = {
  // API
  navAPIByZapier: 'navApiByZapier',

  // MCP
  miscMcp: 'miscMCP',
  navMcp: 'navMCP',

  // AI
  actionAiFix: 'actionAIFix',
  actionAiText: 'actionAIText',
  actionEditAi: 'actionEditAI',
  actionEditAiFill: 'actionEditAIFill',
  miscAi: 'miscAI',
  miscAiFill: 'miscAIFill',
  miscAiGuardrails: 'miscAIGuardrails',
  miscEvergreenAi: 'miscEvergreenAI',
  navAiChatbot: 'navAIChatbot',
} as const satisfies Record<string, IconCanonicalName>;

/**
 * An alternate-casing alias for an icon. See `iconNameAliases`.
 */
export type IconNameAlias = keyof typeof iconNameAliases;

/**
 * Valid names for Zinnia Icons. Intended for use as a React prop type.
 *
 * Includes both canonical names (e.g. `navApiByZapier`) and casing-only
 * aliases (e.g. `navAPIByZapier`). Both resolve to the same icon at render
 * time via `resolveIconName`.
 *
 * @example
 * type MyComponentProps = {
 *   icon: IconName;
 *   children: ReactElement;
 * }
 */
export type IconName = IconCanonicalName | IconNameAlias;

function isAlias(name: IconName): name is IconNameAlias {
  return name in iconNameAliases;
}

/**
 * Resolve an `IconName` (which may be an alias) to its canonical form, suitable
 * for indexing into `allIcons`.
 */
export function resolveIconName(name: IconName): IconCanonicalName {
  return isAlias(name) ? iconNameAliases[name] : name;
}
